import { useState } from "react";
import { toast } from "sonner";
import { Link2, Unlink, Search } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { SERVICE_TYPES } from "../../lib/labels";
import { Chip } from "../common";

const PRINTED = [["del_pckgs", "Colis livrés"], ["del_stops", "Stops livrés"], ["pu_pckgs", "Colis enlevés"], ["pu_stops", "Stops enlevés"]];

function Candidate({ c, r, onLink }) {
  return (
    <div className={`rounded-lg border p-2 text-sm space-y-1 ${c.diffs.length ? "border-red-300 bg-red-50/50" : "border-emerald-300 bg-emerald-50/40"}`} data-testid={`match-${c.ref}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <b className="tl-mono">{c.ref}</b> {c.company_name} · {c.driver_name}
        {c.same_employee && <Chip tone="green">Employee ID</Chip>}{c.same_route && <Chip tone="green">Route ID</Chip>}
        {!c.same_company && <Chip tone="red">Sous-traitant différent ou non attribué</Chip>}
        {c.linked_report && <Chip tone="amber">Déjà rapprochée d'un autre rapport</Chip>}
        <button className="ml-auto tl-btn tl-btn-dark tl-btn-sm" onClick={() => onLink(c)} disabled={!!c.linked_report} data-testid={`match-link-${c.ref}`}><Link2 size={14} /> Rapprocher</button>
      </div>
      <div className="text-xs">Déclaré : {c.types.map((t) => SERVICE_TYPES[t]).join(" + ")}</div>
      <table className="text-xs w-full">
        <thead><tr><th className="text-left">Champ</th><th>Déclaration</th><th>PM Report (imprimé)</th></tr></thead>
        <tbody>{PRINTED.map(([k, l]) => (
          <tr key={k} className={c.diffs.includes(k) ? "text-red-700 font-semibold" : ""}><td>{l}</td><td className="text-center tl-mono">{c.qty[k] ?? "—"}</td><td className="text-center tl-mono">{r.printed[k] ?? "—"}</td></tr>
        ))}</tbody>
      </table>
      {c.diffs.length > 0 && <p className="text-xs text-red-700">Écart : posez une question précise avant de valider (par ex. « Les {r.printed.del_stops ?? "?"} stops sont-ils le total de la prestation ou les stops normaux en plus des Express+ ? »).</p>}
    </div>
  );
}

export function MatchPanel({ r, onChanged }) {
  const [res, setRes] = useState(null);
  const search = async () => {
    try { const { data } = await api.get(`/pm/reports/${r.id}/matches`); setRes(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const link = async (c) => {
    try {
      const { data } = await api.post(`/pm/reports/${r.id}/link`, { declaration_id: c.declaration_id, prestation_id: c.prestation_id });
      toast.success(data.applied ? `Rapproché de ${c.ref} : prestation mise à jour` : `Rapproché de ${c.ref}`);
      setRes(null);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const unlink = async () => {
    try { await api.post(`/pm/reports/${r.id}/unlink`); toast.success("Rapport détaché"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (r.link) {
    return (
      <div className="flex items-center gap-2 text-sm bg-emerald-50 rounded-lg p-2" data-testid={`report-linked-${r.id}`}>
        <Link2 size={16} className="text-emerald-700" /> Rapproché de <b className="tl-mono">{r.link.ref}</b>
        {r.applied_at && <span className="text-xs text-slate-500">· appliqué par {r.applied_by}</span>}
        <button className="ml-auto tl-btn tl-btn-ghost tl-btn-sm" onClick={unlink} data-testid={`report-unlink-${r.id}`}><Unlink size={14} /> Détacher</button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={search} data-testid={`report-matches-${r.id}`}><Search size={14} /> Chercher la déclaration</button>
      {res && <p className="text-xs text-slate-600" data-testid={`report-matches-msg-${r.id}`}>{res.message || `${res.candidates.length} déclaration(s) possible(s) — la société n'est jamais déduite du seul Employee ID.`}</p>}
      {res?.candidates.map((c) => <Candidate key={c.prestation_id} c={c} r={r} onLink={link} />)}
      {!res && <p className="text-xs text-slate-500">Sans déclaration, ce rapport alimente le contrôle côté UPS mais ne valide pas le montant dû au sous-traitant.</p>}
    </div>
  );
}
